/**
 * Get the bar out of the way.
 *
 * The brand, the controls and the row of heritages are furniture. They have to be
 * there when a visitor wants them and gone when they do not, and a fixed bar that is
 * permanently lit sits on top of every photograph on the page — which is the opposite
 * of what the index is for.
 *
 * So it behaves the way a header is expected to: present at the top of the page,
 * gone once you are reading downwards, back the moment you scroll up. Scrolling up is
 * the gesture people already make when they want a way out, and it costs nothing to
 * honour it.
 *
 * Hiding is deliberately not tied to reduced motion. Someone who has asked for less
 * animation has not asked for a bar across their photographs; the stylesheet drops the
 * transition for them and the bar simply is, or is not, there.
 */

export type IndexBar = { dispose: () => void };

/** Within this far of the top, the bar is always shown: you have not left yet. */
const AT_TOP = 64;
/** Travel below this is a trackpad tremor, not a change of mind. */
const TRAVEL = 10;

export function revealOnScrollUp(scroller: HTMLElement, bar: HTMLElement): IndexBar {
  let last = scroller.scrollTop;
  // Two variables for what looks like one fact. The handle is only ever needed to
  // cancel, and it is assigned *after* requestAnimationFrame returns — so a callback
  // that runs during that call would have its own reset overwritten, and nothing would
  // ever be scheduled again. The flag is set before the call and cleared inside it.
  let pending = false;
  let frame = 0;

  const show = (visible: boolean): void => {
    bar.dataset.hidden = visible ? "false" : "true";
  };
  show(true);

  const paint = (): void => {
    pending = false;
    const now = scroller.scrollTop;
    // Checked before the travel threshold, so easing back to the top always restores
    // the bar even when the last few pixels are too small to count as a gesture.
    if (now <= AT_TOP) {
      last = now;
      show(true);
      return;
    }
    const moved = now - last;
    // Not updating `last` here is what lets small movements accumulate into one.
    if (Math.abs(moved) < TRAVEL) return;
    last = now;
    show(moved < 0);
  };

  const onScroll = (): void => {
    if (pending) return;
    pending = true;
    frame = requestAnimationFrame(paint);
  };
  // A control reached by keyboard has to be visible to whoever reached it, and tabbing
  // does not scroll far enough to bring the bar back on its own.
  const onFocus = (): void => show(true);

  scroller.addEventListener("scroll", onScroll, { passive: true });
  bar.addEventListener("focusin", onFocus);

  return {
    dispose: () => {
      scroller.removeEventListener("scroll", onScroll);
      bar.removeEventListener("focusin", onFocus);
      if (pending) cancelAnimationFrame(frame);
      pending = false;
    },
  };
}
